import React, {useEffect, useState} from 'react';
import './Settings.scss';
import {useProfileStore} from "../../store/profile/pofile.store";
import {getCachedPlans, open_billing_portal, process_by_id, subscriptions_get_all} from "../../api/subscriptions_api";
import {auth_get_profile} from "../../api/auth_api";
import SubscriptionCard from "../../components/ui/PriceCard/PriceCard";
import Avatar from "../../components/ui/Avatar/Avatar";
import {CheckIcon, XIcon, Mail, Crown, LogOut, KeyRound, Trash2, AlertTriangle, Cookie} from 'lucide-react';
import {toast} from "react-toastify";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useCurrentChatStore} from "../../store";
import {
    firebase_sign_out,
    hasPasswordSignIn,
    sendPasswordReset,
    sendVerificationEmail
} from "../../services/firebase_services";
import DeleteAccountModal from "../../components/layout/DeleteAccount/DeleteAccountModal";

const PROFILE_MAX_AGE_MS = 60_000;

// First day of next month (usage resets per calendar month, UTC)
const nextResetDate = () => {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
        .toLocaleDateString(undefined, {month: 'long', day: 'numeric', timeZone: 'UTC'});
};

const UsageMeter = ({label, used, limit}: { label: string, used: number, limit: number }) => {
    const percent = limit ? Math.min(100, Math.round(used / limit * 100)) : 100;
    return (
        <div className="UsageMeter">
            <div className="UsageMeter__row">
                <span>{label}</span>
                <span className="UsageMeter__value"><b>{used.toLocaleString()}</b> / {limit.toLocaleString()}</span>
            </div>
            <div className={`UsageMeter__bar ${percent >= 90 ? 'UsageMeter__bar--warning' : ''}`}>
                <span style={{width: `${percent}%`}}/>
            </div>
        </div>
    );
};

const Settings = () => {

    const {user, clearUser, updateUser}: any = useProfileStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const {resetChat} = useCurrentChatStore();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState<any[]>(() => getCachedPlans() || []);
    const [redirecting, setRedirecting] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [passwordLinkSent, setPasswordLinkSent] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const plan = user?.plan;
    const usesPassword = hasPasswordSignIn();

    const changePassword = () => {
        sendPasswordReset(user?.email).then(() => {
            setPasswordLinkSent(true);
            toast.success(`We sent a password link to ${user?.email}`);
        }).catch((err) => {
            toast.error(err?.code === 'auth/too-many-requests'
                ? 'Too many emails sent. Please wait a few minutes and try again.'
                : 'Could not send the email. Please try again.');
        });
    };

    const resendVerification = () => {
        sendVerificationEmail().then(() => {
            setVerificationSent(true);
            toast.success(`Verification email sent to ${user?.email}`);
        }).catch((err) => {
            toast.error(err?.code === 'auth/too-many-requests'
                ? 'Too many emails sent. Please wait a few minutes and try again.'
                : 'Could not send the email. Please sign out, sign in again and retry.');
        });
    };

    const signOutUser = async () => {
        await firebase_sign_out();
        resetChat();
        clearUser();
        navigate('/auth');
    };

    useEffect(() => {
        subscriptions_get_all().then(setSubscriptions).catch(() => undefined);
    }, []);

    // Usage changes as the user chats on other devices and resets each month, so reload a profile older than a minute
    useEffect(() => {
        // Coming back from Checkout reloads the profile below anyway
        if (new URLSearchParams(window.location.search).get('checkout') === 'success') return;
        if (Date.now() - useProfileStore.getState().loadedAt < PROFILE_MAX_AGE_MS) return;
        auth_get_profile().then((res) => updateUser(res.data)).catch(() => undefined);
    }, [updateUser]);

    // Back from Stripe Checkout: thank the user and reload the plan (the webhook may take a moment)
    useEffect(() => {
        if (searchParams.get('checkout') !== 'success') return;
        toast.success('Welcome to Plus! Your new limits are active.');
        setSearchParams({}, {replace: true});
        auth_get_profile().then((res) => updateUser(res.data)).catch(() => undefined);
    }, [searchParams, setSearchParams, updateUser]);

    const openPortal = () => {
        if (redirecting) return;
        setRedirecting(true);
        open_billing_portal().then((res: any) => {
            window.location.href = res.data.url;
        }).catch(() => {
            toast.error('Could not open subscription settings, please try again');
            setRedirecting(false);
        });
    };

    const start_process_by_id = (id: string) => {
        if (redirecting) return;
        setRedirecting(true);
        process_by_id(id).then((res: any) => {
            window.location.href = res.data.url
        }).catch(() => {
            toast('Could not open checkout, please try again', {type: "error"});
            setRedirecting(false);
        })
    }

    if (!user) return null;

    return (
        <div className="Settings">
            <div className="Settings__content">

                <section className="card Settings__profile">
                    {user?.name && <Avatar type={'name'} image={user?.name}/>}
                    <div className="Settings__profileInfo">
                        <h3>{user?.name}</h3>
                        <div className="Settings__email">
                            <Mail size={15}/>
                            <span>{user?.email}</span>
                            {user?.verified ?
                                <span className="Settings__badge"><CheckIcon size={13}/> Verified</span>
                                : <>
                                    <span className="Settings__badge Settings__badge--warning"><XIcon size={13}/> Not verified</span>
                                    <button type="button" className="Settings__link" onClick={resendVerification}
                                            disabled={verificationSent}>
                                        {verificationSent ? 'Email sent' : 'Resend link'}
                                    </button>
                                </>
                            }
                        </div>
                    </div>
                    <button type="button" className="Settings__signOut" onClick={signOutUser}>
                        <LogOut size={16}/> Sign out
                    </button>
                </section>

                {
                    plan && <section className="card Settings__plan">
                        <div className="Settings__planHeader">
                            <div className="icon-tile icon-tile--gradient"><Crown size={18}/></div>
                            <div>
                                <span className="eyebrow">Your plan</span>
                                <h3>{plan.name}</h3>
                            </div>
                            <span className="Settings__reset">Usage resets on {nextResetDate()}</span>
                            {
                                user?.stripe_customer_id && (plan.paid || user?.subscription) &&
                                <button type="button" className="Settings__action" onClick={openPortal}
                                        disabled={redirecting}>
                                    Manage subscription
                                </button>
                            }
                        </div>

                        {
                            user?.payment_issue && <div className="Settings__alert" role="alert">
                                <AlertTriangle size={18}/>
                                <span>Your last payment didn't go through, so you're on the Free limits for now.
                                    Update your card to get Plus back.</span>
                                <button type="button" className="Settings__action Settings__action--danger"
                                        onClick={openPortal} disabled={redirecting}>
                                    Update card
                                </button>
                            </div>
                        }

                        <div className="Settings__usage">
                            <UsageMeter label="Messages this month" used={plan.usage.messagesSent}
                                        limit={plan.limits.messagesPerMonth}/>
                            <UsageMeter label="Voice replies this month" used={plan.usage.voiceMessageSent}
                                        limit={plan.limits.voiceMessagesPerMonth}/>
                            <UsageMeter label="AI chats" used={user.chats?.length || 0} limit={plan.limits.maxChats}/>
                        </div>
                    </section>
                }

                <section className="Settings__plans">
                    <div className="Settings__sectionTitle">
                        <h3>Plans</h3>
                        <p>Get more chats, messages and voice replies. Billed monthly, cancel anytime.</p>
                    </div>

                    <div className="Settings__subscriptions">
                        {
                            plan && !plan.paid && <SubscriptionCard isCurrent data={{
                                name: 'Free',
                                amount: 0,
                                currency: subscriptions[0]?.currency || 'usd',
                                interval: 'month',
                                limits: plan.limits
                            }}/>
                        }
                        {subscriptions.map((el: any) => {
                            const isCurrent = el?.productId === user?.subscription?.product;
                            return (
                                <SubscriptionCard key={el.productId} data={el} isCurrent={isCurrent}
                                                  hasSubscription={Boolean(user?.subscription)}
                                                  onClick={isCurrent ? undefined : () => start_process_by_id(el.priceId)}/>
                            );
                        })}
                    </div>
                </section>

                <section className="card Settings__account">
                    <div className="Settings__sectionTitle">
                        <h3>Account</h3>
                    </div>

                    {
                        usesPassword && <div className="Settings__accountRow">
                            <div className="icon-tile"><KeyRound size={18}/></div>
                            <div className="Settings__accountText">
                                <b>Password</b>
                                <span>We'll email you a link to set a new password.</span>
                            </div>
                            <button type="button" className="Settings__action" onClick={changePassword}
                                    disabled={passwordLinkSent}>
                                {passwordLinkSent ? 'Link sent' : 'Change password'}
                            </button>
                        </div>
                    }

                    <div className="Settings__accountRow">
                        <div className="icon-tile"><Cookie size={18}/></div>
                        <div className="Settings__accountText">
                            <b>Cookies</b>
                            <span>Choose whether we may use analytics cookies.</span>
                        </div>
                        <button type="button" className="Settings__action"
                                onClick={() => (window as any).soulifyCookieSettings?.()}>
                            Cookie settings
                        </button>
                    </div>

                    <div className="Settings__accountRow">
                        <div className="icon-tile Settings__dangerIcon"><Trash2 size={18}/></div>
                        <div className="Settings__accountText">
                            <b>Delete account</b>
                            <span>Permanently delete your account, chats and cloned voices.</span>
                        </div>
                        <button type="button" className="Settings__action Settings__action--danger"
                                onClick={() => setDeleteModal(true)}>
                            Delete account
                        </button>
                    </div>
                </section>

                {deleteModal && <DeleteAccountModal closeMenu={() => setDeleteModal(false)}/>}

            </div>
        </div>
    );
};

export default Settings;
